import { useCallback, useMemo, useState } from "react";
import {
  bestGradientTextColor,
  bestTextColor,
  Default_Accent_From,
  Default_Accent_Text,
  Default_Accent_To,
  isValidHexColor,
} from "../../../../../../common/infrastructure/utilities/colorUtility";
import {
  DesignStudioFamilyId,
  IDesignStudioColors,
  IDesignStudioPreset,
} from "../models/designStudioPresetModel";
import {
  designStudioDefaultColors,
  findMatchingPreset,
} from "../utils/designStudioPaletteUtility";
import { getDesignStudioPaletteName } from "../utils/designStudioPreviewUtility";

export type DesignStudioMode = "presets" | "advanced";
export type DesignStudioFamilyFilter = DesignStudioFamilyId | "all";
export type DesignStudioColorKey = keyof IDesignStudioColors;

type AccentKey = "accentColorFrom" | "accentColorTo" | "accentTextColor";

interface IPaletteState {
  colors: IDesignStudioColors;
  autoButtonText: boolean;
  autoAccentText: boolean;
}

// What an accent stop falls back to while the palette has none: the built-in purple → pink.
const builtInAccent: Record<AccentKey, string> = {
  accentColorFrom: Default_Accent_From,
  accentColorTo: Default_Accent_To,
  accentTextColor: Default_Accent_Text,
};

const accentKeys = Object.keys(builtInAccent) as AccentKey[];

const bare = (hex: string | null | undefined) =>
  (hex ?? "").trim().replace(/^#/, "").toLowerCase();

const isAccentKey = (key: DesignStudioColorKey): key is AccentKey =>
  accentKeys.includes(key as AccentKey);

export const hasOwnAccentColors = (colors: IDesignStudioColors) =>
  isValidHexColor(colors.accentColorFrom) && isValidHexColor(colors.accentColorTo);

const isAutoButtonText = (colors: IDesignStudioColors) =>
  !isValidHexColor(colors.buttonBackgroundColor) ||
  bare(colors.buttonTextColor) === bestTextColor(colors.buttonBackgroundColor);

const isAutoAccentText = (colors: IDesignStudioColors) =>
  !hasOwnAccentColors(colors) ||
  !bare(colors.accentTextColor) ||
  bare(colors.accentTextColor) ===
    bestGradientTextColor(colors.accentColorFrom, colors.accentColorTo);

// Auto text is on when the colours already are what auto would pick: a studio that never
// touched them keeps following its button, one that chose them by hand keeps its choice.
const stateFrom = (colors: IDesignStudioColors): IPaletteState => ({
  colors: { ...colors },
  autoButtonText: isAutoButtonText(colors),
  autoAccentText: isAutoAccentText(colors),
});

const withAutoText = (state: IPaletteState): IPaletteState => {
  const colors = { ...state.colors };

  if (state.autoButtonText && isValidHexColor(colors.buttonBackgroundColor)) {
    colors.buttonTextColor = bestTextColor(colors.buttonBackgroundColor);
  }

  if (state.autoAccentText && hasOwnAccentColors(colors)) {
    colors.accentTextColor = bestGradientTextColor(
      colors.accentColorFrom,
      colors.accentColorTo,
    );
  }

  return { ...state, colors };
};

export const useDesignStudio = (
  initialColors: IDesignStudioColors,
  initialMode: DesignStudioMode = "presets",
) => {
  const [palette, setPalette] = useState<IPaletteState>(() => stateFrom(initialColors));
  const [mode, setMode] = useState<DesignStudioMode>(initialMode);
  const [familyFilter, setFamilyFilter] = useState<DesignStudioFamilyFilter>("all");

  const { colors, autoButtonText, autoAccentText } = palette;

  const matchedPreset = useMemo(() => findMatchingPreset(colors), [colors]);
  const paletteName = useMemo(() => getDesignStudioPaletteName(colors), [colors]);
  const hasOwnAccent = hasOwnAccentColors(colors);

  const selectPreset = useCallback((preset: IDesignStudioPreset) => {
    setPalette(stateFrom(preset.colors));
  }, []);

  const setColor = useCallback((key: DesignStudioColorKey, hex: string) => {
    const value = bare(hex);

    if (!isValidHexColor(value)) {
      return;
    }

    setPalette((previous) => {
      const next: IPaletteState = {
        ...previous,
        colors: { ...previous.colors, [key]: value },
      };

      // A colour picked by hand wins over auto.
      if (key === "buttonTextColor") {
        next.autoButtonText = false;
      }
      if (key === "accentTextColor") {
        next.autoAccentText = false;
      }

      // The accent is all or nothing: picking one part of an empty accent fills the rest
      // with the built-in colours it was showing, so the pair is always complete.
      if (isAccentKey(key) && !hasOwnAccentColors(previous.colors)) {
        accentKeys
          .filter((other) => other !== key && !isValidHexColor(previous.colors[other]))
          .forEach((other) => {
            next.colors[other] = builtInAccent[other];
          });
      }

      return withAutoText(next);
    });
  }, []);

  const setAutoButtonText = useCallback((on: boolean) => {
    setPalette((previous) => withAutoText({ ...previous, autoButtonText: on }));
  }, []);

  const setAutoAccentText = useCallback((on: boolean) => {
    setPalette((previous) => withAutoText({ ...previous, autoAccentText: on }));
  }, []);

  const applyButtonToAccent = useCallback(() => {
    setPalette((previous) => ({
      ...stateFrom({
        ...previous.colors,
        accentColorFrom: previous.colors.buttonBackgroundColor,
        accentColorTo: previous.colors.buttonBackgroundColor,
        accentTextColor: previous.colors.buttonTextColor,
      }),
      // Only the accent changed; the button's auto-text switch stays where the user put it.
      autoButtonText: previous.autoButtonText,
    }));
  }, []);

  const clearAccent = useCallback(() => {
    setPalette((previous) => ({
      ...previous,
      colors: {
        ...previous.colors,
        accentColorFrom: "",
        accentColorTo: "",
        accentTextColor: "",
      },
      autoAccentText: true,
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setPalette(stateFrom(designStudioDefaultColors));
  }, []);

  const reset = useCallback(
    (nextColors: IDesignStudioColors, nextMode: DesignStudioMode = "presets") => {
      setPalette(stateFrom(nextColors));
      setMode(nextMode);
      setFamilyFilter("all");
    },
    [],
  );

  return {
    colors,
    mode,
    familyFilter,
    autoButtonText,
    autoAccentText,
    matchedPreset,
    paletteName,
    hasOwnAccent,
    setMode,
    setFamilyFilter,
    selectPreset,
    setColor,
    setAutoButtonText,
    setAutoAccentText,
    applyButtonToAccent,
    clearAccent,
    resetToDefaults,
    reset,
  };
};

export type IDesignStudioState = ReturnType<typeof useDesignStudio>;
